import React, { useEffect, useState } from "react";
import { Coins, DownloadSimple, Receipt } from "@phosphor-icons/react";
import { api, API_BASE } from "@/lib/api";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function InstructorEarningsCard() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [year, setYear] = useState(null);

  useEffect(() => {
    api.get("/instructor/earnings", { params: year ? { year } : {} })
      .then(r => { setData(r.data); if (year === null && r.data.years?.length) setYear(Number(r.data.years[0])); })
      .catch(() => {});
  }, [year]);
  useEffect(() => { api.get("/instructor/payouts").then(r => setPayouts(r.data)).catch(() => {}); }, []);

  if (!data) return null;
  const t = data.totals;

  return (
    <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="instructor-earnings-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          <Coins size={20} weight="fill" color="#FFC400" /> Earnings
        </h3>
        {year && (
          <a data-testid="earnings-pdf-link" className="btn-ghost text-xs flex items-center gap-1 border border-[#C4C8CE] rounded-full px-3 py-1.5 hover:border-[#CCFF00]"
            href={`${API_BASE}/instructor/earnings/pdf?year=${year}`} target="_blank" rel="noreferrer">
            <DownloadSimple size={13} weight="bold" /> {year} summary PDF
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        {[["All time", t.all_time_cents, "earnings-all-time"],
          ["This month", t.this_month_cents, "earnings-this-month"],
          ["Last 12 months", t.last_12mo_cents, "earnings-12mo"],
          ["Pending payout", t.pending_balance_cents, "earnings-pending"]].map(([label, cents, tid]) => (
          <div key={tid} className="rounded-xl border border-[#EDEEF0] bg-[#F9FAFB] p-3" data-testid={tid}>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#000000]">{label}</div>
            <div className="font-display font-extrabold text-lg">{money(cents)}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-[#000000] mt-2" data-testid="next-payout-date">Next payout run: {data.next_payout_date}</div>

      {data.years.length > 0 && (
        <div className="flex gap-1.5 mt-4 flex-wrap" data-testid="earnings-year-chips">
          {data.years.map(y => (
            <button key={y} data-testid={`earnings-year-${y}`}
              className={`text-xs font-bold px-3 py-1 rounded-full border cursor-pointer ${String(year) === y ? "bg-[#CCFF00] border-[#CCFF00] text-[#000000]" : "bg-white border-[#C4C8CE] text-[#000000]"}`}
              onClick={() => setYear(Number(y))}>{y}</button>
          ))}
        </div>
      )}

      {data.lines.length > 0 ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="earnings-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[#000000] border-b border-[#EDEEF0]">
              <th className="py-1.5 pr-2">Date</th><th className="pr-2">Course</th><th className="pr-2">Sale</th>
              <th className="pr-2">Role</th><th className="pr-2">%</th><th className="pr-2 text-right">Earned</th><th></th>
            </tr></thead>
            <tbody>
              {data.lines.map(l => {
                const claw = l.kind === "clawback" || l.payout_status === "clawed_back";
                return (
                  <tr key={l.id} className={`border-b border-[#F4F4F5] ${claw ? "text-[#B91C1C]" : ""}`} data-testid={`earning-line-${l.id}`}>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{l.created_at?.slice(0, 10)}</td>
                    <td className="pr-2 max-w-[180px] truncate">{l.course_title || "—"}</td>
                    <td className="pr-2">{money(l.sale_cents)}</td>
                    <td className="pr-2 uppercase text-xs font-bold">{l.role_in_chain}</td>
                    <td className="pr-2">{l.pct}%</td>
                    <td className="pr-2 text-right font-semibold">{money(l.cents)}</td>
                    <td className="text-[10px]">
                      {l.kind === "clawback" ? <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] font-bold">CLAWBACK</span>
                        : l.payout_status === "clawed_back" ? <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] font-bold">REFUNDED</span>
                        : l.payout_status === "paid" ? <span className="px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534] font-bold">PAID</span>
                        : <span className="px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-bold">PENDING</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-[#000000] mt-3" data-testid="earnings-empty">No attributed sales yet — share your referral link or promo codes to start earning.</p>
      )}

      {payouts.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#EDEEF0]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#000000] mb-1.5 flex items-center gap-1"><Receipt size={12} /> Payout statements</div>
          {payouts.map(p => (
            <a key={p.id} data-testid={`payout-statement-${p.id}`}
              className="flex items-center justify-between text-sm py-1.5 border-b border-[#F4F4F5] hover:text-[#000000] no-underline text-[#000000]"
              href={`${API_BASE}/payouts/${p.id}/pdf`} target="_blank" rel="noreferrer">
              <span>{p.period} · {p.line_count} line{p.line_count === 1 ? "" : "s"}</span>
              <span className="font-bold flex items-center gap-1">{money(p.amount_cents)} <DownloadSimple size={13} /></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
